/**
 * DocsHubCard — Carte Google Docs du Hub Workspace.
 *
 * Règle utilisateur : MANUEL uniquement.
 *  - Liste les templates Google Docs présents dans /System/Templates/ (Drive)
 *  - Bouton "Générer Document" qui copie le template choisi dans le dossier de l'expertise
 *  - Aucun envoi/replacement automatique — l'admin éditera dans Google Docs
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FileText, Loader2, Plus, ExternalLink, X, Link2, Save } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


export default function DocsHubCard({ masterId, expertise, expertiseActivated, manualUrl, onLinked }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [docName, setDocName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  // Batch #14.32 — Lien manuel Docs (URL scratchpad/document principal)
  const [editingLink, setEditingLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState(manualUrl || "");
  const [savingLink, setSavingLink] = useState(false);

  const saveManualLink = async () => {
    const trimmed = (linkUrl || "").trim();
    if (!trimmed) {
      toast.error("Collez l'URL d'un Google Doc");
      return;
    }
    setSavingLink(true);
    try {
      await axios.put(
        `${API}/masters/${masterId}/expertises/${expertise}/workspace-tool/docs/link`,
        { url: trimmed },
        { headers: authH() },
      );
      toast.success("Document lié ✓");
      setEditingLink(false);
      onLinked?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur — URL invalide");
    } finally {
      setSavingLink(false);
    }
  };

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/masters/${masterId}/templates`, { headers: authH() });
      setTemplates(r.data?.items || []);
    } catch (e) {
      // Silent — affichera "Aucun template" si la liste est vide
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!selectedTemplateId) {
      toast.error("Sélectionnez un template");
      return;
    }
    setGenerating(true);
    try {
      const params = new URLSearchParams({ template_id: selectedTemplateId });
      if (docName.trim()) params.append("doc_name", docName.trim());
      const r = await axios.post(
        `${API}/masters/${masterId}/expertises/${expertise}/generate-doc?${params.toString()}`,
        {},
        { headers: authH() },
      );
      setLastGenerated(r.data);
      toast.success(`Document « ${r.data.doc_name} » créé ✓`);
      setShowModal(false);
      setSelectedTemplateId("");
      setDocName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération");
    } finally {
      setGenerating(false);
    }
  };

  if (!expertiseActivated) {
    return (
      <div
        className="border border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/40"
        data-testid="masters-hub-docs-disabled"
      >
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 flex-shrink-0 text-slate-400" />
          <div>
            <div className="font-semibold text-sm text-slate-700">Google Docs</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Activez l'expertise pour générer des documents
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="border border-slate-200 rounded-xl p-4 bg-white hover:border-blue-300 hover:shadow-sm transition"
        data-testid="masters-hub-docs"
      >
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 flex-shrink-0 text-blue-600" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-slate-800">Google Docs</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {loading ? "Chargement…" : `${templates.length} template(s) disponible(s)`}
            </div>
            {lastGenerated && (
              <a
                href={lastGenerated.doc_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-emerald-600 mt-1 inline-flex items-center gap-0.5 hover:text-emerald-800"
              >
                <ExternalLink className="w-3 h-3" /> Ouvrir « {lastGenerated.doc_name?.slice(0, 28)}… »
              </a>
            )}
            {/* Batch #14.32 — Lien manuel vers un Google Doc */}
            {manualUrl && !editingLink && (
              <a
                href={manualUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-blue-600 mt-1 inline-flex items-center gap-0.5 hover:text-blue-800"
                data-testid="masters-hub-docs-manual-link"
              >
                <ExternalLink className="w-3 h-3" /> Ouvrir mon doc lié
              </a>
            )}
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setShowModal(true)}
                disabled={loading || templates.length === 0}
                className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs inline-flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-40"
                data-testid="masters-hub-docs-open-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Générer Document
              </button>
              <button
                onClick={() => { setEditingLink(true); setLinkUrl(manualUrl || ""); }}
                className="px-2.5 py-1.5 rounded-lg border border-blue-200 bg-white text-blue-700 text-xs inline-flex items-center gap-1.5 hover:bg-blue-50"
                data-testid="masters-hub-docs-link-btn"
              >
                <Link2 className="w-3.5 h-3.5" /> {manualUrl ? "Modifier le lien" : "Lier un doc"}
              </button>
            </div>
            {editingLink && (
              <div className="mt-2 space-y-1.5" data-testid="masters-hub-docs-link-form">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://docs.google.com/document/d/..."
                  className="w-full px-2.5 py-1.5 text-xs border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                  data-testid="masters-hub-docs-link-input"
                  autoFocus
                />
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={saveManualLink}
                    disabled={savingLink}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs inline-flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-40"
                    data-testid="masters-hub-docs-link-save"
                  >
                    {savingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer
                  </button>
                  <button
                    onClick={() => setEditingLink(false)}
                    disabled={savingLink}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs hover:bg-slate-50 disabled:opacity-40"
                  >
                    Annuler
                  </button>
                </div>
                <div className="text-[10px] text-slate-400">
                  💡 Créez un Google Doc, copiez son URL et collez-la ici (scratchpad client).
                </div>
              </div>
            )}
            {!loading && templates.length === 0 && (
              <p className="text-[10px] text-slate-400 italic mt-1">
                Déposez des Google Docs dans /System/Templates/
              </p>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="masters-hub-docs-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold inline-flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" /> Générer un document
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-500 font-medium">Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full px-2.5 py-2 text-sm border rounded-lg mt-0.5"
                  data-testid="masters-hub-docs-select"
                >
                  <option value="">— Choisir un template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 font-medium">
                  Nom du nouveau document <span className="text-slate-400">(optionnel)</span>
                </label>
                <input
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="ex: Mandat fiscal 2025"
                  className="w-full px-2.5 py-2 text-sm border rounded-lg mt-0.5"
                  data-testid="masters-hub-docs-name-input"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Si vide : Document_{"{couple}"}_{expertise}_{"{horodatage}"}
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-[11px] text-blue-900">
                Le document sera copié dans le dossier Drive de l'expertise <b>{expertise}</b>.
                <span className="block text-blue-700 mt-0.5">
                  Aucun placeholder ne sera remplacé automatiquement — vous éditerez dans Google Docs.
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 text-sm rounded-lg border"
              >
                Annuler
              </button>
              <button
                onClick={generate}
                disabled={generating || !selectedTemplateId}
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-blue-700"
                data-testid="masters-hub-docs-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Générer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
